<script setup lang="ts">
// Shared chrome for the task inputs/gates/successors editors: a title with a JSON toggle, then either the
// JSON textarea (power-user escape hatch) or the structured rows/add-row markup in the default slot.
// Extracted because TaskGatesEditor/TaskInputsEditor/TaskSuccessorsEditor otherwise repeat this same
// toggle-button + textarea + error-message block verbatim; see useRowsModel.ts for the state it renders.
// The JSON box is the kit `Textarea` (#1227): it states no border, fill or radius of its own.
import { Button, Textarea } from '@zerohuman/ui'

defineProps<{
    /** Shared prefix for this section's data-testids, e.g. "task-gates" -> "task-gates-json-toggle". */
    testid: string
    title: string
    showJson: boolean
    jsonText: string
    jsonError: string | null
    /** Textarea height; gates fit in fewer lines than inputs or successors. */
    jsonRows?: number
}>()
defineEmits<{ 'update:jsonText': [string]; toggle: [] }>()
</script>

<template>
	<div :data-testid="`${testid}-editor`" class="space-y-2">
		<div class="flex flex-wrap items-center justify-between gap-2">
			<p class="text-sm font-medium">{{ title }}</p>
			<Button
				type="button"
				size="sm"
				variant="outline"
				:data-testid="`${testid}-json-toggle`"
				@click="$emit('toggle')"
			>
				{{ showJson ? 'Back to fields' : 'JSON' }}
			</Button>
		</div>
		<p class="text-muted-foreground text-xs"><slot name="description" /></p>
		<template v-if="showJson">
			<Textarea
				:model-value="jsonText"
				:data-testid="`${testid}-json-box`"
				:rows="jsonRows ?? 4"
				@update:model-value="$emit('update:jsonText', $event)"
			/>
			<p v-if="jsonError" class="text-destructive text-xs">{{ jsonError }}</p>
		</template>
		<template v-else>
			<slot />
		</template>
	</div>
</template>
